import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	signal,
} from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	applyValidators,
	CurrencySymbolPipe,
	DestroyService,
	DialogBase,
	DialogOptions,
	DialogService,
} from '@marmon/frontend/common';
import { ConfigService } from '@marmon/frontend/config';
import { LoggerService } from '@marmon/frontend/logging';
import {
	Analysis,
	AnalysisCostType,
	AnalysisCostTypes,
	evaluateParsedResults,
	formatRawInvoiceData,
	Invoice,
	ParseInvoiceError,
	rawInvoicesToCsv,
	TIME_PERIOD_MAX,
	TimePeriod,
	TooManyRowsError,
} from '@marmon/shared/types/analysis';
import {
	Currencies,
	Currency,
	numberToCents,
} from '@marmon/shared/types/common';
import { TuiDay } from '@taiga-ui/cdk';
import {
	TuiButton,
	TuiError,
	TuiHint,
	TuiIcon,
	TuiInput,
	TuiLoader,
	TuiNotification,
	TuiNumberFormat,
} from '@taiga-ui/core';
import {
	TuiButtonLoading,
	TuiChevron,
	TuiDataListWrapper,
	TuiFiles,
	TuiInputDate,
	TuiInputFiles,
	TuiInputNumber,
	TuiProgress,
	TuiSelect,
} from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { Timestamp } from 'firebase/firestore';
import { BehaviorSubject, filter, takeUntil } from 'rxjs';
import { CreateAnalysisErrorsDialogComponent } from '../';
import { AnalysisApiService } from '../../services';
import {
	getAnalysisExpiration,
	parseFile,
	readPreferredCurrency,
	savePreferredCurrency,
	yieldForPaint,
} from '../../utils';

type FormTimePeriod = {
	currency: Currency;
	minDate: TuiDay;
	maxDate: TuiDay;
	overheadCosts: number;
};

type AnalysisForm = {
	currency: Currency;
	name: string;
	timePeriods: FormTimePeriod[];
	costType: AnalysisCostType;

	file?: File;
};

/**
 * Note that we want to instantiate the new form with a random ID. This is because, when we parse
 * the file we need to create document references for all of the products, customers, and segments
 * without needing an additional iteration of the entire invoice dataset. So we need what the ID of
 * the Analysis will be so we can assign that document reference of the nested collection at the
 * creation of the invoice.
 */
@Component({
	selector: 'mar-create-analysis-dialog',
	standalone: true,
	imports: [
		TuiFiles,
		FormsModule,
		ReactiveFormsModule,
		TuiInput,
		TuiInputFiles,
		AsyncPipe,
		TuiIcon,
		TranslocoPipe,
		TuiError,
		TuiInputDate,
		TuiInputNumber,
		CurrencySymbolPipe,
		TuiButton,
		TuiButtonLoading,
		TuiLoader,
		TuiProgress,
		TuiNotification,
		TuiDataListWrapper,
		TuiNumberFormat,
		TuiHint,
		TuiSelect,
		TuiChevron,
	],
	templateUrl: 'create-analysis-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DestroyService],
	host: { class: 'relative block' },
})
export class CreateAnalysisDialogComponent extends applyValidators(
	DialogBase<Partial<Analysis>, null>,
) {
	static override DialogOptions = {
		size: 'l',
		header: 'analyses.dialog.create-analysis.header',
		dismissible: false,
	} as DialogOptions;

	private readonly destroy$ = inject(DestroyService);
	private readonly dialog = inject(DialogService);
	private readonly config = inject(ConfigService);
	private readonly analysisApiService = inject(AnalysisApiService);
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly logger = inject(LoggerService);

	form: FormGroup | null = null;

	readonly costTypes = AnalysisCostTypes;
	readonly currencies = Currencies;

	/**
	 * A valid file will consist of the following:
	 * 	- correct file type
	 * 	- formatted correctly
	 * 	- required column headers (extra columns are ignored)
	 * 	- all necessary data
	 * 	- within the configured file size and row-count limits
	 *
	 * After a successful parse we replace the form file with a CSV that
	 * contains only the required columns before it is uploaded to Storage.
	 */
	hasValidFile = false;

	loading = false;

	/**
	 * Increments on each file-change so an in-flight parse cannot clear the
	 * loader after a newer file has already started (or been removed).
	 */
	private parseRequestId = 0;

	/**
	 * the progress that the system has during the data upload.
	 */
	uploadProgress$ = new BehaviorSubject({
		value: 0,
		complete: false,
		uploading: false,
	});

	/**
	 * the absolute maximum day that any of the time periods could possibly be
	 */
	maxDate: TuiDay | null = null;

	/**
	 * the absolute minimum day that any of the time periods could possibly be
	 */
	minDate: TuiDay | null = null;

	/**
	 * array of invoices gathered after the file is parsed. This will only non-null
	 * if a file was provided and was parsed correctly. If there were errors then this will
	 * remain null until a correct file was supplied.
	 */
	invoices: Invoice[] | null = null;

	products: string[] = [];
	customers: string[] = [];
	segments: string[] = [];

	readonly maxRowCount = signal<number>(0);

	createAnalysisErrorsDialog = (
		errors: ParseInvoiceError[],
		fileName: string,
	) =>
		this.dialog.open(
			new PolymorpheusComponent(CreateAnalysisErrorsDialogComponent),
			CreateAnalysisErrorsDialogComponent.Options({
				data: { errors, fileName },
			}),
		);

	get timePeriods() {
		if (!this.form) return null;
		return this.form.get('timePeriods') as FormArray;
	}

	constructor() {
		super();
		this.setupForm();
	}

	setupForm() {
		this.config.getNumber('max_row_count').then((value) => {
			this.maxRowCount.set(value);
		});

		const expiresAt = getAnalysisExpiration();
		TuiDay.fromLocalNativeDate(expiresAt.toDate());

		this.invoices = null;
		this.form = new FormGroup({
			id: new FormControl(crypto.randomUUID()),
			currency: new FormControl(readPreferredCurrency(), [
				(field) => this.getRequiredValidator(field),
			]),
			// note that this expiration is transient, the true expiration date will happen
			// during the creation to avoid real-time day change or leaving window open for a few days
			expiresAt: new FormControl(
				TuiDay.fromLocalNativeDate(expiresAt.toDate()),
			),
			name: new FormControl(null, [
				(field) =>
					this.getRequiredValidator(
						field,
						'analyses.dialog.create-analysis.form.require-name',
					),
			]),
			costType: new FormControl(null, (field) =>
				this.getRequiredValidator(field),
			),
			file: new FormControl(null as File | null, [
				(field) =>
					this.getRequiredValidator(
						field,
						'analyses.dialog.create-analysis.form.require-file',
					),
			]),
			timePeriods: new FormArray(
				this.createTimePeriodGroup(TIME_PERIOD_MAX),
			),
		});

		this.form
			.get('file')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((value: File | null) => {
				void this.onFileSelected(value);
			});

		this.form
			.get('currency')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((value) => savePreferredCurrency(value));

		if (this.timePeriods) {
			const maxDateControl = this.timePeriods
				.at(0)
				.get('maxDate') as AbstractControl<TuiDay>;
			const minDateControl = this.timePeriods
				.at(1)
				.get('minDate') as AbstractControl<TuiDay>;

			maxDateControl.valueChanges
				.pipe(
					takeUntil(this.destroy$),
					filter((value) => !!value),
				)
				.subscribe((value) => {
					const { year, month, day } = value;
					const date = new Date(year, month, day + 1);
					minDateControl.patchValue(TuiDay.fromUtcNativeDate(date), {
						emitEvent: false,
					});
				});

			minDateControl.valueChanges
				.pipe(
					takeUntil(this.destroy$),
					filter((value) => !!value),
				)
				.subscribe((value) => {
					const { year, month, day } = value;
					const date = new Date(year, month, day - 1);
					maxDateControl.patchValue(TuiDay.fromUtcNativeDate(date), {
						emitEvent: false,
					});
				});
		}
	}

	private async onFileSelected(value: File | null) {
		this.invoices = null;
		this.hasValidFile = false;

		const requestId = ++this.parseRequestId;
		if (!value) {
			this.loading = false;
			this.cdRef.detectChanges();
			return;
		}

		this.loading = true;
		this.cdRef.detectChanges();
		await this.waitForPaint();

		if (requestId !== this.parseRequestId) return;

		try {
			await this.parseFile(value);
		} catch (error) {
			this.logger.warn('Analysis CSV parse did not complete', {
				source: 'CreateAnalysisDialog.parseFile',
				extra: { error },
			});
		} finally {
			if (requestId === this.parseRequestId) {
				this.loading = false;
				this.cdRef.detectChanges();
			}
		}
	}

	/**
	 * Lets the loader overlay paint before PapaParse blocks the main thread.
	 */
	private waitForPaint(): Promise<void> {
		return yieldForPaint();
	}

	async parseFile(file: File) {
		if (!this.form || !this.form.get('id'))
			throw Error('A form with an ID value is required');

		const invokeErrorDialog = (errors: ParseInvoiceError[]) => {
			this.hasValidFile = false;
			this.createAnalysisErrorsDialog(
				errors,
				createName(file.name),
			).subscribe({
				complete: () => this.onRemoveFile(),
			});
			this.cdRef.detectChanges();
			return Promise.reject(
				'The provided CSV has errors that need to be resolved.',
			);
		};

		// first we need to parse and prep the file that was provided.
		const maxRows = this.maxRowCount();
		const parsed = await parseFile(file);

		if (!maxRows || parsed.data.length > maxRows) {
			return invokeErrorDialog([
				TooManyRowsError(parsed.data.length, maxRows),
			]);
		}

		const evaluation = evaluateParsedResults(parsed);
		const { result, errors } = formatRawInvoiceData(evaluation, {
			now: Timestamp.now(),
		});

		if (errors) {
			return invokeErrorDialog(errors);
		}

		this.form.get('file')?.setValue(
			new File([rawInvoicesToCsv(evaluation.data)], file.name, {
				type: 'text/csv',
			}),
			{ emitEvent: false },
		);

		const { invoices, minDate, maxDate, products, customers, segments } =
			result;
		if (!invoices || !invoices.length)
			throw Error('No data determined after finding no errors');

		const timePeriod1Min = this.timePeriods?.at(0) as AbstractControl;
		this.minDate = TuiDay.fromUtcNativeDate(minDate);

		timePeriod1Min.patchValue({ minDate: this.minDate });
		timePeriod1Min.get('minDate')?.disable();

		const timePeriod2Max = this.timePeriods?.at(-1) as AbstractControl;
		this.maxDate = TuiDay.fromUtcNativeDate(maxDate);

		timePeriod2Max.patchValue({ maxDate: this.maxDate });
		timePeriod2Max.get('maxDate')?.disable();

		const nameControl = this.form?.get('name');
		// only override the name if it hasn't been inputted yet
		if (nameControl && !nameControl.value) {
			nameControl.setValue(createName(file.name));
		}

		this.form.get('expiresAt')?.disable();

		this.hasValidFile = true;
		this.invoices = invoices;
		this.products = products;
		this.customers = customers;
		this.segments = segments;
		this.cdRef.detectChanges();
		return Promise.resolve();
	}

	onSubmit() {
		this.form?.markAllAsTouched();

		const uploadProgress = this.uploadProgress$.value;
		if (
			!this.form ||
			!this.form.valid ||
			uploadProgress.uploading ||
			!this.invoices
		) {
			return;
		}

		this.uploadProgress$.next({ ...uploadProgress, uploading: true });
		this.form.disable({ emitEvent: false });

		const value = this.form.getRawValue() as AnalysisForm;
		const timePeriods = createTimePeriodFromForm(
			value.timePeriods as [FormTimePeriod, FormTimePeriod],
		);

		const file = value.file as File;

		const { invoices, segments } = this;
		return this.analysisApiService
			.create(
				{ ...value, timePeriods },
				{ file, invoices, segments },
				(progress) => {
					this.uploadProgress$.next({
						uploading: true,
						complete: progress.complete,
						value: progress.percent,
					});
				},
			)
			.then((value: Partial<Analysis>) => {
				this.uploadProgress$.next({
					uploading: false,
					complete: true,
					value: 100,
				});
				this.cdRef.detectChanges();

				// arbitrary timeout for UI change, dismiss afterwards
				setTimeout(() => {
					this.onDismiss(value);
				}, 1500);
			})
			.catch((error) => {
				console.error('CreateAnalysisDialog.onSubmit', error);
				throw Error('There was an error creating the analysis');
			})
			.finally(() => {
				this.form?.enable({ emitEvent: false });

				// We need to ensure that these values remain disabled since they are automatically
				// detected from the raw invoice data being imported.
				const timePeriod1Min = this.timePeriods?.at(
					0,
				) as AbstractControl;
				timePeriod1Min.get('minDate')?.disable();

				const timePeriod2Max = this.timePeriods?.at(
					-1,
				) as AbstractControl;
				timePeriod2Max.get('maxDate')?.disable();

				this.form?.get('expiresAt')?.disable();
			});
	}

	readonly createTimePeriodGroup = (count: number) =>
		Array.from(Array(count)).map(
			() =>
				new FormGroup({
					currency: new FormControl(readPreferredCurrency()),
					minDate: new FormControl(null, [
						(field) => this.getRequiredValidator(field),
					]),
					maxDate: new FormControl(null, [
						(field) => this.getRequiredValidator(field),
					]),
					overheadCosts: new FormControl(null, [
						(field) => this.getRequiredValidator(field),
					]),
				}),
		);

	readonly onRemoveFile = () => this.form?.reset();
}

function createName(fileName: string) {
	const name = fileName.split('.');
	name.pop();
	return name.join('');
}

const createTimePeriodFromForm = (
	periods: [FormTimePeriod, FormTimePeriod],
): [TimePeriod, TimePeriod] =>
	periods.map((period) => {
		const { minDate, maxDate } = period;
		const min = minDate.toUtcNativeDate().getTime();
		const max = maxDate.toUtcNativeDate().getTime();

		return {
			currency: period.currency,
			minDate: Timestamp.fromMillis(min),
			maxDate: Timestamp.fromMillis(max),
			overheadCostInCents: numberToCents(period.overheadCosts),
			overheadCostInCentsPerTransaction: 0,
			transactionCount: 0,
		} as TimePeriod;
	}) as [TimePeriod, TimePeriod];
